import type { QueryClient } from "@tanstack/react-query";

import {
  adoptAttachment,
  CASE_KEY,
  CASES_KEY,
  ProblemError,
  refusalCode,
  type AttachmentOut,
  type CorrespondenceOut,
} from "@/api";

/**
 * „Als Zeichnung übernehmen" — the only way from a mail to the reading
 * pipeline (design §4.3), from an entry's menu. Filing, not a way on
 * (round-3 screen check, R3-1): the head no longer offers it. The answer is
 * one sentence; `done` says whether the drawing is under „Zeichnungen" now.
 * 409 `document_exists`: the drawing is already there; 409 `retracted`: the
 * entry was retracted meanwhile (Plan B); 404: the Vorgang was handed over a
 * moment ago (Plan C) — the record reloads into „nicht gefunden".
 */
export async function adoptIntoDrawings(
  client: QueryClient,
  caseId: string,
  entry: CorrespondenceOut,
  attachment: AttachmentOut,
): Promise<{ sentence: string; done: boolean }> {
  try {
    await adoptAttachment(caseId, entry.id, attachment.id);
    // `CASE_KEY` covers the record and the document list of the Vorgang;
    // the register shows the adoption as the last activity (T9).
    void client.invalidateQueries({ queryKey: CASE_KEY(caseId) });
    void client.invalidateQueries({ queryKey: CASES_KEY });
    return {
      sentence: `${attachment.filename} liegt jetzt unter Zeichnungen — als Kopie, die Mail bleibt in der Akte.`,
      done: true,
    };
  } catch (cause) {
    const code = refusalCode(cause);
    const gone = cause instanceof ProblemError && cause.status === 404;
    if (gone || code === "retracted") void client.invalidateQueries({ queryKey: CASE_KEY(caseId) });
    return {
      sentence:
        code === "document_exists"
          ? `${attachment.filename} liegt schon unter Zeichnungen.`
          : code === "retracted"
            ? "Der Eintrag ist zurückgezogen — seine Anlagen lassen sich nicht übernehmen."
            : gone
              ? "Der Vorgang liegt inzwischen nicht mehr bei dir."
              : `${attachment.filename} nicht übernommen — ${cause instanceof ProblemError ? cause.userMessage : "Server nicht erreichbar."}`,
      done: false,
    };
  }
}
